/**
 * The page a headless browser prints — Architecture §12.3.
 *
 * Nobody navigates here. A render token in the URL buys one snapshot, and this
 * draws it with the same `ReportDocument` the instructor approved in the
 * preview, from the same origin, with the same stylesheet and the same fonts.
 * That is what makes "the PDF is what you saw" true rather than intended.
 *
 * No shell, no session, no navigation: the browser that opens this is a
 * renderer, not a person.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { ReportSnapshot, SnapshotPhoto } from '@pp/shared';
import { ReportDocument } from '@pp/report-template';
import '@pp/report-template/styles.css';
import './ReportRender.css';

interface RenderPayload {
  snapshot: ReportSnapshot;
  /** Photographs already resolved to data: URIs — the renderer has no session. */
  images: Record<string, string>;
}

type State =
  | { status: 'loading' }
  | { status: 'ready'; payload: RenderPayload }
  | { status: 'failed'; message: string };

/*
 * A render token is spendable exactly once, so this page must ask for it
 * exactly once. React's development double-mount would otherwise spend the
 * token on the first request and get a 404 on the second — the page renders
 * the failure, and the studio receives an empty PDF. Keyed by token and held
 * outside the component, so a remount joins the first request instead of
 * making a second one.
 */
const payloads = new Map<string, Promise<RenderPayload>>();

function loadPayload(token: string): Promise<RenderPayload> {
  const existing = payloads.get(token);
  if (existing) return existing;

  // Deliberately a bare fetch rather than the shared client: that one carries
  // credentials and speaks the session's error vocabulary, and this request
  // has no session to carry.
  const request = fetch(`/api/reports/render/${token}`).then(async (response) => {
    if (!response.ok) throw new Error(`render payload ${response.status}`);
    return (await response.json()) as RenderPayload;
  });

  payloads.set(token, request);
  return request;
}

export function ReportRender() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<State>({ status: 'loading' });
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    let cancelled = false;

    loadPayload(token ?? '')
      .then((payload) => {
        if (!cancelled) setState({ status: 'ready', payload });
      })
      .catch((error: unknown) => {
        if (!cancelled) setState({ status: 'failed', message: String(error) });
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  /*
   * The renderer waits for data-report-ready before printing. Raising it early
   * produces a PDF with blank photo frames and fallback type, so it waits for
   * the fonts to load and every image to decode — and then one more frame, so
   * the layout those fonts changed has actually been painted.
   */
  useEffect(() => {
    if (state.status !== 'ready') return;
    let cancelled = false;

    async function settle() {
      await document.fonts.ready;

      const images = [...document.querySelectorAll('img')];
      await Promise.all(
        images.map((image) =>
          image.complete
            ? Promise.resolve()
            : new Promise<void>((resolve) => {
                image.addEventListener('load', () => resolve(), { once: true });
                // A photograph that will not decode must not hold the report
                // hostage; the frame renders empty and the rest is still true.
                image.addEventListener('error', () => resolve(), { once: true });
              }),
        ),
      );

      await new Promise((resolve) => requestAnimationFrame(resolve));
      if (!cancelled) setSettled(true);
    }

    void settle();
    return () => {
      cancelled = true;
    };
  }, [state]);

  if (state.status === 'loading') return <div className="render render--waiting" />;

  if (state.status === 'failed') {
    // Marked ready so the renderer stops waiting and fails fast with a blank
    // page rather than timing out after a minute.
    return (
      <div className="render render--failed" data-report-ready="true">
        Rapor yüklenemedi.
      </div>
    );
  }

  const { snapshot, images } = state.payload;

  return (
    <div className="render" data-report-ready={settled ? 'true' : 'false'}>
      <ReportDocument
        snapshot={snapshot}
        resolveImage={(photo: SnapshotPhoto) => images[photo.photoId] ?? null}
        // The logo travels under a fixed key; it has no photo id of its own.
        logoUrl={images['studio-logo'] ?? null}
      />
    </div>
  );
}
